import { PmiButton } from 'shell/components/shared/button';
import { memo, useCallback, useState } from 'react';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { setNestedObjectValues, useFormikContext } from 'formik';
import { AuthorizationVoteFormVM } from 'front-office/view-models/authorization-vote';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ELECTORAL_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { AuthorizationVote, AuthorizationVoteData } from 'shell/models/person/electoral-card';
import { DateTime } from 'luxon';
import DeleteAlertModal from 'system/components/shared/delete-alert';

export function mapAuthorizationVote(form: AuthorizationVoteFormVM): AuthorizationVote {
  const electoralData = form.electoralData || {};

  const datiAutorizzazioneAlVoto: AuthorizationVoteData | undefined =
    form.authorizationData?.milan || form.authorizationData?.outsideMilan
      ? {
          numeroProtocollo: electoralData.datiAutorizzazioneAlVoto?.numeroProtocollo || 0,
          annoProtocollo: electoralData.datiAutorizzazioneAlVoto?.annoProtocollo || '',
          registroProtocollo: electoralData.datiAutorizzazioneAlVoto?.registroProtocollo || '',
          luogoCuraMilano: form.authorizationData?.milan
            ? {
                sezione: Number(form.authorizationData?.milan?.section || 0),
                descrizione: form.authorizationData?.milan?.description || undefined,
                dataRichiesta: form.authorizationData?.milan?.requestDate?.toISODate() || undefined,
                zona: form.authorizationData?.milan?.zone || undefined,
                collegioCamera: Number(form.authorizationData?.milan?.collegioCamera || 0),
                collegioSenato: Number(form.authorizationData?.milan?.collegioSenato || 0),
                collegioProvinciale: Number(
                  form.authorizationData?.milan?.collegioProvinciale || 0
                ),
              }
            : undefined,
          luogoCuraFuoriMilano: form.authorizationData?.outsideMilan
            ? {
                luogo: form.authorizationData?.outsideMilan?.place || undefined,
                dataRichiesta:
                  form.authorizationData?.outsideMilan?.requestDate?.toISODate() || undefined,
                comune: form.authorizationData?.outsideMilan?.municipality
                  ? {
                      key: {
                        codice: form.authorizationData?.outsideMilan?.municipality?.code,
                      },
                      nome:
                        form.authorizationData?.outsideMilan?.municipality?.description ||
                        undefined,
                      codIstat: form.authorizationData?.outsideMilan?.municipality?.id || undefined,
                      provincia:
                        form.authorizationData?.outsideMilan?.municipality?.province || undefined,
                    }
                  : undefined,
              }
            : undefined,
        }
      : undefined;

  return {
    ...(electoralData.electoralDataModel || {}),
    flagCertificatoAutorizzazione: form.isAuthorized ?? 'N',
    tipoConsultazione: electoralData.tipoConsultazione || undefined,
    consultazione: electoralData.consultazione || undefined,
    dataDa: electoralData.dataDa?.toFormat('yyyy-MM-dd') || undefined,
    dataA: electoralData.dataA?.toFormat('yyyy-MM-dd') || undefined,
    datiAutorizzazioneAlVoto,
    matricola: electoralData.matricola,
    annoAttoNascita: Number(electoralData.actYear || 0) || undefined,
    numeroAttoNascita: Number(electoralData.actNumber || 0) || undefined,
    parteAttoNascita: electoralData.actPart || undefined,
    serieAttoNascita: electoralData.actSerie || undefined,
    volumeAttoNascita: electoralData.actVolume || undefined,
    codiceFiscale: electoralData.fiscalCode || undefined,
    cognome: electoralData.surname || undefined,
    nome: electoralData.name || undefined,
    dataNascita: electoralData.dateOfBirth
      ? DateTime.fromFormat(electoralData.dateOfBirth, 'dd/MM/yyyy').toFormat('yyyy-MM-dd')
      : undefined,
    stato: electoralData.electorState || undefined,
    collegioCamera: electoralData.room || undefined,
    collegioSenato: electoralData.senate || undefined,
    collegioProvinciale: electoralData.province || undefined,
    zona: electoralData.zone || undefined,
    indirizzo: electoralData.address || undefined,
    numeroCivico: electoralData.civic || undefined,
    nomeVia: electoralData.addressName || undefined,
    descrizioneLuogoResidenza: electoralData.residenceDescription || undefined,
    provNazLuogoResidenza: electoralData.provNatPlaceResidence || undefined,
    statoTesseraPrecedente: electoralData.previousCardState || undefined,
    tesseraNuova: electoralData.newCardNumber || undefined,
  };
}

interface PrintAuthorizationVoteButtonsProps {
  onExit: () => void;
  getElectoralPersonData: () => void;
}

const PrintAuthorizationVoteButtonsComponent = ({
  onExit,
  getElectoralPersonData,
}: PrintAuthorizationVoteButtonsProps): React.ReactElement => {
  const formikContext = useFormikContext<AuthorizationVoteFormVM>();

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const [warningModal, setWarningModal] = useState<boolean>(false);

  const saveAuthorizationVote = useHttpClient<void>(
    `${__ELECTORAL_API_URL__}/persona/autorizzazione-al-voto/salva`,
    {
      options: {
        errorCatching: true,
        loading: true,
        method: 'POST',
      },
    }
  );

  const printAuthorizationVote = useHttpClient<Blob>(
    `${__ELECTORAL_API_URL__}/persona/autorizzazione-al-voto/stampa`,
    {
      options: {
        errorCatching: true,
        loading: true,
        method: 'POST',
      },
    }
  );

  const cancelAuthorizationVote = useHttpClient<Blob>(
    `${__ELECTORAL_API_URL__}/persona/autorizzazione-al-voto/annulla`,
    {
      options: {
        errorCatching: true,
        loading: true,
        method: 'POST',
      },
    }
  );

  const handleCancel = useCallback(() => {
    formikContext.validateForm(formikContext.values).then((validationErrors) => {
      formikContext.setTouched(setNestedObjectValues(validationErrors, true));

      if (Object.values(validationErrors).length > 0) {
        return;
      }

      setWarningModal(true);
    });
  }, [cancelAuthorizationVote, setWarningModal]);

  const triggerCancel = useCallback(() => {
    formikContext.validateForm(formikContext.values).then((validationErrors) => {
      formikContext.setTouched(setNestedObjectValues(validationErrors, true));

      if (Object.values(validationErrors).length > 0) {
        return;
      }

      const data: AuthorizationVote = mapAuthorizationVote(formikContext.values);
      (cancelAuthorizationVote({ data }) as ResponsePromise<void>).then(() => {
        getElectoralPersonData();
        setWarningModal(false);
        toast.success('Operazione eseguita con successo');
      });
    });
  }, [cancelAuthorizationVote, onExit, getElectoralPersonData]);

  const handleSave = useCallback(() => {
    formikContext.validateForm(formikContext.values).then((validationErrors) => {
      formikContext.setTouched(setNestedObjectValues(validationErrors, true));

      if (Object.values(validationErrors).length > 0) {
        return;
      }

      const data: AuthorizationVote = mapAuthorizationVote(formikContext.values);
      (saveAuthorizationVote({ data }) as ResponsePromise<void>).then(() => {
        onExit();
        toast.success('Operazione eseguita con successo');
      });
    });
  }, [saveAuthorizationVote, onExit]);

  const handlePrint = useCallback(() => {
    formikContext.validateForm(formikContext.values).then((validationErrors) => {
      formikContext.setTouched(setNestedObjectValues(validationErrors, true));

      if (Object.values(validationErrors).length > 0) {
        return;
      }

      const data: AuthorizationVote = mapAuthorizationVote(formikContext.values);
      (printAuthorizationVote({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then(
        (response) => {
          const pdfCertificate = response?.data;
          if (pdfCertificate) {
            const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
            blobToBase64(blob).then((base64) => {
              const name = 'Autorizzazione al voto';
              setPdfModal({
                data: base64.split(',')[1],
                name: formikContext.values.isAuthorized === 'S' ? name : `Non ${name}`,
                open: true,
              });
            });
          }
        }
      );
    });
  }, [printAuthorizationVote, formikContext, setPdfModal]);

  return (
    <>
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title={pdfModal.name}
          onClose={() => {
            setPdfModal({ data: '', open: false, name: '' });
            onExit();
          }}
        />
      ) : undefined}
      {warningModal ? (
        <DeleteAlertModal
          message="Confermi l'annullamento del certificato?"
          open={warningModal}
          title="Annullamento certificato autorizzazione"
          onYes={triggerCancel}
          onNo={() => {
            setWarningModal(false);
          }}
          onClose={() => {
            setWarningModal(false);
          }}
        />
      ) : undefined}
      <StickyDiv>
        <div>
          {formikContext.values.isAuthorized === 'S' ? (
            <>
              <div className="pmi-pb-2">
                <PmiButton
                  color="cdmBrandThird"
                  variant="contained"
                  onClick={handlePrint}
                  fullWidth
                  id="print"
                >
                  Stampa Certificato AUTORIZZAZIONE in pdf
                </PmiButton>
              </div>
              <div className="pmi-pb-2">
                <PmiButton
                  color="cdmBrandThird"
                  variant="contained"
                  onClick={handleSave}
                  fullWidth
                  id="save"
                >
                  Salva Certificato AUTORIZZAZIONE
                </PmiButton>
              </div>
            </>
          ) : formikContext.values.isAuthorized === 'N' ? (
            <>
              <div className="pmi-pb-2">
                <PmiButton
                  color="cdmBrandThird"
                  variant="contained"
                  onClick={handlePrint}
                  fullWidth
                  id="print"
                >
                  Stampa Certificato NON AUTORIZZAZIONE in pdf
                </PmiButton>
              </div>
              <div className="pmi-pb-2">
                <PmiButton
                  color="cdmBrandThird"
                  variant="contained"
                  onClick={handleSave}
                  fullWidth
                  id="save"
                >
                  Salva Certificato NON AUTORIZZAZIONE
                </PmiButton>
              </div>
            </>
          ) : (
            <div className="pmi-pb-2">
              <PmiButton
                color="cdmBrandThird"
                onClick={handleCancel}
                variant="contained"
                fullWidth
                id="cancel"
              >
                Annulla Certificato
              </PmiButton>
            </div>
          )}
          <div className="pmi-pb-2">
            <PmiButton
              color="cdmBrand"
              variant="contained"
              fullWidth
              onClick={onExit}
              id="deny-operation"
            >
              Indietro
            </PmiButton>
          </div>
        </div>
      </StickyDiv>
    </>
  );
};

PrintAuthorizationVoteButtonsComponent.displayName = 'Print authorization vote buttons component';

const PrintAuthorizationVoteButtons = memo(PrintAuthorizationVoteButtonsComponent);

export default PrintAuthorizationVoteButtons;
